import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Breadcrumbs, Link, Typography, Menu, MenuItem, useTheme, useMediaQuery } from '@mui/material';
import Home from '@mui/icons-material/Home';
import ChevronRight from '@mui/icons-material/ChevronRight';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { useTranslation } from 'react-i18next';
import { useWikiTypes } from '../../../contexts/WikiTypesContext';
import { useWikiItem } from '../../../contexts/WikiItemContext';
import { parseWikiUrl } from '../../../utils/wikiUrlParser';
import { TEXT_COLORS, BG_COLORS, BORDER_COLORS } from '../../../utils/colors';

interface WikiBreadcrumbsProps {
  // Optional custom breadcrumb items
  customItems?: Array<{
    label: string;
    path?: string;
    icon?: React.ReactNode;
  }>;
  // Disable background when used inside another container
  noBackground?: boolean;
}

const WikiBreadcrumbs: React.FC<WikiBreadcrumbsProps> = ({ customItems, noBackground = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('wiki');
  const { findHierarchy } = useWikiTypes();
  const { currentItemName } = useWikiItem();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const lastWidthRef = useRef(0);
  // 0 — все пункты; 1 — у «Базы знаний» только иконка; 2 — иконка + «…» + последний (как на мобиле).
  // Ступень выбирается по факту нехватки места, а не по брейкпоинту: ширина строки зависит от того,
  // открыта ли боковая панель детали (левая колонка сужается вдвое), от языка и от глубины цепочки.
  const [collapseStage, setCollapseStage] = useState(0);
  const [, forceRemeasure] = useState(0);

  // Parse RESTful URL structure outside of buildBreadcrumbs
  const urlParams = parseWikiUrl(location.pathname);

  const buildBreadcrumbs = () => {
    const items: Array<{
      label: string;
      path?: string;
      icon?: React.ReactNode;
    }> = [
      {
        label: t('title'),
        path: '/wiki',
        icon: <Home sx={{ mr: 0.5, ml: { xs: 0.5, sm: 0 }, fontSize: { xs: '1rem', sm: 'inherit' } }} />
      }
    ];

    if (customItems) {
      items.push(...customItems);
      return items;
    }

    if (!urlParams) {
      return items;
    }

    const { source, typeId, subtypeId, itemId, subcategoryId, raceId, skillTypeId, classId, professionId } = urlParams;

    // Use universal findHierarchy to get full path
    const hierarchy = findHierarchy({
      apiSource: source,
      subcategoryId,
      typeId,
      subtypeId,
      raceId,
      skillTypeId,
      classId,
      professionId
    });

    // === SKILLS PATH ===
    if (source === 'skills') {
      if (hierarchy.category) {
        items.push({
          label: hierarchy.category.name,
          path: '/wiki/skills'
        });
      }
      if (hierarchy.race) {
        items.push({
          label: hierarchy.race.name,
          path: `/wiki/skills/races/${hierarchy.race.id}`
        });
      }
      if (hierarchy.skillType) {
        items.push({
          label: hierarchy.skillType.name,
          path: `/wiki/skills/races/${hierarchy.race!.id}/types/${hierarchy.skillType.id}`
        });
      }
      if (hierarchy.class) {
        items.push({
          label: hierarchy.class.name,
          path: `/wiki/skills/races/${hierarchy.race!.id}/types/${hierarchy.skillType!.id}/classes/${hierarchy.class.id}`
        });
      }
      if (hierarchy.profession) {
        items.push({
          label: hierarchy.profession.name,
          path: `/wiki/skills/races/${hierarchy.race!.id}/types/${hierarchy.skillType!.id}/classes/${hierarchy.class!.id}/professions/${hierarchy.profession.id}`
        });
      }
    }
    // === NPCs PATH (no types, only subcategories) ===
    else if (source === 'npcs') {
      if (hierarchy.category) {
        items.push({
          label: hierarchy.category.name,
          path: '/wiki/npcs'
        });
      }
      if (hierarchy.subCategory) {
        items.push({
          label: hierarchy.subCategory.name,
          path: `/wiki/npcs/subcategories/${hierarchy.subCategory.id}`
        });
      }
    }
    // === STANDARD PATH (weapons/armors/items) ===
    else {
      if (hierarchy.category) {
        items.push({
          label: hierarchy.category.name,
          path: `/wiki/${hierarchy.category.apiSource}`
        });
      }
      if (hierarchy.subCategory) {
        items.push({
          label: hierarchy.subCategory.name,
          // Use subcategoryId for unique identification
          path: `/wiki/${source}/subcategories/${hierarchy.subCategory.id}`
        });
      }
      if (hierarchy.type) {
        items.push({
          label: hierarchy.type.name,
          // Include subcategoryId in path
          path: hierarchy.subCategory
            ? `/wiki/${source}/subcategories/${hierarchy.subCategory.id}/types/${hierarchy.type.id}`
            : `/wiki/${source}/types/${hierarchy.type.id}`
        });
      }
      if (hierarchy.subType) {
        items.push({
          label: hierarchy.subType.name,
          path: hierarchy.subCategory
            ? `/wiki/${source}/subcategories/${hierarchy.subCategory.id}/types/${hierarchy.type!.id}/subtypes/${hierarchy.subType.id}`
            : `/wiki/${source}/types/${hierarchy.type!.id}/subtypes/${hierarchy.subType.id}`
        });
      }
    }

    // Add item breadcrumb (current item, no link) for detail pages
    if (itemId && currentItemName) {
      items.push({
        label: currentItemName
        // No path for current item
      });
    }

    return items;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path: string) => {
    handleMenuClose();
    navigate(path);
  };

  const breadcrumbItems = buildBreadcrumbs();

  // Следим за РОДИТЕЛЕМ, а не за самими крошками: ширину корня задаёт содержимое (в flex-строке
  // с minWidth:0 он ужимается по контенту), а ширину родителя — колонка страницы.
  useEffect(() => {
    const container = rootRef.current?.parentElement;
    if (!container) return;
    const observer = new ResizeObserver(() => forceRemeasure((tick) => tick + 1));
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Меряем на КАЖДОМ рендере, а не по списку зависимостей: панель детали открывается без смены
  // маршрута и без изменения самих крошек — менялась только ширина, и завязанный на state эффект
  // такой случай пропускал (строка просто уезжала под overflow).
  useLayoutEffect(() => {
    const root = rootRef.current;
    const ol = root?.querySelector('.MuiBreadcrumbs-ol');
    if (!root || !ol) return;

    // Стало шире/уже — сначала пробуем снова показать всё, и только потом схлопываем по замеру.
    // Ширину берём у РОДИТЕЛЯ: ширина самих крошек меняется от схлопывания, и сравнение с ней
    // зацикливалось (схлопнули → корень сузился → «ширина изменилась» → развернули → и по кругу).
    const containerWidth = (root.parentElement ?? root).clientWidth;
    if (containerWidth !== lastWidthRef.current) {
      lastWidthRef.current = containerWidth;
      if (collapseStage !== 0) {
        setCollapseStage(0);
        return;
      }
    }
    // Ступеней всего две, каждая укорачивает строку — цикл конечен
    if (collapseStage < 2 && ol.scrollWidth > ol.clientWidth + 1) {
      setCollapseStage(collapseStage + 1);
    }
  });

  // For mobile: get collapsed items (middle items that will be hidden)
  const getCollapsedItems = () => {
    if (breadcrumbItems.length <= 2) return [];
    return breadcrumbItems.slice(1, -1);
  };

  // For mobile: get items to display (icon only + "..." + last)
  const getMobileItems = () => {
    if (breadcrumbItems.length <= 2) {
      // Show only icon for first item on mobile
      return breadcrumbItems.map((item, idx) =>
        idx === 0 ? { ...item, label: '' } : item
      );
    }
    return [
      { ...breadcrumbItems[0], label: '' },  // Only icon, no text
      { label: '...', isCollapsed: true },
      breadcrumbItems[breadcrumbItems.length - 1]
    ];
  };

  const collapsedItems = getCollapsedItems();
  const displayItems =
    isMobile || collapseStage >= 2
      ? getMobileItems()
      : collapseStage === 1
        // Первой жертвой становится подпись «База знаний» — иконка дома и так однозначна
        ? breadcrumbItems.map((item, idx) => (idx === 0 ? { ...item, label: '' } : item))
        : breadcrumbItems;

  return (
    <>
      <Breadcrumbs
        ref={rootRef}
        separator={<ChevronRight fontSize="small" sx={{
          '@media (max-width: 950px)': {
            fontSize: '0.9rem',
          },
          '@media (max-width: 450px)': {
            fontSize: '0.8rem',
          },
        }} />}
        sx={{
          // Крошки часто стоят flex-элементом (на странице детали — рядом с кнопкой «Назад»).
          // Без minWidth:0 такой элемент не сжимается ниже своего содержимого: строка вылезает
          // за контейнер и обрезается, а замер переполнения её не видит — ol «влезает» в самого себя.
          minWidth: 0,
          ...(noBackground ? {} : {
            mb: 3,
            p: 2,
            background: BG_COLORS.GLASS,
            border: `1px solid ${BORDER_COLORS.LIGHT}`,
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 'none',
              borderColor: 'rgba(185, 28, 28, 0.6)',
              background: BG_COLORS.GLASS_LIGHTER,
            },
            '@media (max-width: 1200px)': {
              mb: 2,
              p: 1.5,
            },
            '@media (max-width: 950px)': {
              mb: 1.5,
              p: 1,
            },
            '@media (max-width: 450px)': {
              mb: 1,
              p: 0.75,
            },
          }),
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
            rowGap: 0.5,
            overflow: 'hidden',
          },
          // Пункты не переносятся внутри себя: перенос — это и есть баг, который лечим схлопыванием.
          '& .MuiBreadcrumbs-li': {
            whiteSpace: 'nowrap',
          },
        }}
      >
        {displayItems.map((item: any, index: number) => {
          const isLast = index === displayItems.length - 1;

          // Handle collapsed "..." button
          if (item.isCollapsed) {
            return (
              <Link
                key="collapsed"
                component="button"
                onClick={handleMenuOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: TEXT_COLORS.MUTED,
                  cursor: 'pointer',
                  fontSize: { xs: '0.7rem', sm: '0.8rem', md: '1rem' },
                  minWidth: 'auto',
                  p: 0,
                  '&:hover': {
                    color: 'white',
                  },
                }}
              >
                <MoreHoriz sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />
              </Link>
            );
          }

          if (isLast || !item.path) {
            return (
              <Typography
                key={index}
                color="text.primary"
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: isLast ? 600 : 400,
                  fontSize: '1rem',
                  maxWidth: isLast ? { xs: '290px', sm: '350px', md: '200px', lg: '160px', xl: '380px' } : 'none',
                  '@media (max-width: 1200px)': {
                    fontSize: '0.9rem',
                  },
                  '@media (max-width: 950px)': {
                    fontSize: '0.8rem',
                  },
                  '@media (max-width: 450px)': {
                    fontSize: '0.7rem',
                    maxWidth: isLast ? '150px' : 'none',
                  },
                  '@media (max-width: 380px)': {
                    maxWidth: isLast ? '70px' : 'none',
                  },
                }}
              >
                {item.icon}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.label}
                </span>
              </Typography>
            );
          }

          return (
            <Link
              key={index}
              component="button"
              variant="body1"
              onClick={() => handleNavigation(item.path!)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: TEXT_COLORS.MUTED,
                cursor: 'pointer',
                fontSize: '1rem',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'white',
                },
                '@media (max-width: 1200px)': {
                  fontSize: '0.9rem',
                },
                '@media (max-width: 950px)': {
                  fontSize: '0.8rem',
                },
                '@media (max-width: 450px)': {
                  fontSize: '0.7rem',
                },
              }}
            >
              {item.icon}
              {item.label}
            </Link>
          );
        })}
      </Breadcrumbs>

      {/* Dropdown menu for collapsed breadcrumb items */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            background: BG_COLORS.GLASS,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${BORDER_COLORS.LIGHT}`,
            borderRadius: 2,
            minWidth: 150,
          },
        }}
      >
        {collapsedItems.map((item, index) => (
          <MenuItem
            key={index}
            onClick={() => item.path && handleMenuItemClick(item.path)}
            sx={{
              color: TEXT_COLORS.SECONDARY,
              fontSize: '0.85rem',
              '&:hover': {
                background: BG_COLORS.GLASS_LIGHT,
                color: 'white',
              },
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default WikiBreadcrumbs;